import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { SectionCard } from '@/components/shared/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { BarChart, CartesianGrid, XAxis, YAxis, ReferenceLine, Bar, Cell } from 'recharts'
import type { AnalyticsSummary } from '@/lib/api'
import { fmt, fmtCompact } from './utils'

const waterfallChartConfig = {
  range: { label: 'Valor', color: 'var(--chart-1)' },
} satisfies ChartConfig

export function WaterfallSection({
  summary,
  isLoading,
}: {
  summary: AnalyticsSummary
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    )
  }

  // Waterfall data: each bar floats from its "base" to "base + value"
  const data = [
    { name: '1ª Compra', value: summary.receita1aCompra, base: 0, fill: 'var(--chart-1)' },
    {
      name: 'Recompras',
      value: summary.receitaRecompras,
      base: summary.receita1aCompra,
      fill: 'var(--chart-2)',
    },
    {
      name: 'Descontos',
      value: -summary.descontoConcedido,
      base: summary.receitaTotal,
      fill: 'var(--chart-3)',
    },
    {
      name: 'Cashback',
      value: -summary.cashbackResgatado,
      base: summary.receitaTotal - summary.descontoConcedido,
      fill: 'var(--chart-3)',
    },
    { name: 'Resultado', value: summary.resultadoLiquido, base: 0, fill: 'var(--chart-4)' },
  ]

  // For floating bars, we use [base, base+value]
  const waterfallData = data.map((d) => ({
    name: d.name,
    range: d.value >= 0 ? [d.base, d.base + d.value] : [d.base + d.value, d.base],
    value: d.value,
    fill: d.fill,
  }))

  return (
    <SectionCard
      title="Demonstrativo Financeiro"
      description="Composição da receita, custos e resultado líquido"
    >
      <ChartContainer config={waterfallChartConfig} className="min-h-[300px] w-full">
        <BarChart data={waterfallData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => fmtCompact(v)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(
                  _value: unknown,
                  _name: unknown,
                  item: { payload?: { value: number } }
                ) => fmt(item.payload?.value || 0)}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          <ReferenceLine y={0} stroke="hsl(var(--border))" />
          <Bar dataKey="range" radius={[4, 4, 0, 0]}>
            {waterfallData.map((entry, index) => (
              <Cell key={index} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </SectionCard>
  )
}
